import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {App} from '@capacitor/app';
import {Capacitor, PluginListenerHandle} from '@capacitor/core';
import {Subscription} from 'rxjs';
import {distinctUntilChanged, filter, finalize, map} from 'rxjs/operators';
import {ReputationService} from '../../shared/reputation/reputation.service';
import {TokenDataProviderService} from './token-data-provider.service';
import {NotificationDataProviderService} from './notification.data-provider.service';

/** Visits are writes: never perform them during SSR or for a background document. */
@Injectable({providedIn: 'root'})
export class ReputationVisitsService {
  private subscriptions = new Subscription();
  private nativeListener?: PluginListenerHandle;
  private started = false;
  private userId: number | null = null;
  private pending = false;
  private lastAttempt = 0;
  private readonly onVisibility = () => { if (this.document.visibilityState !== 'hidden') this.visit(); };

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly tokens: TokenDataProviderService,
    private readonly router: Router,
    private readonly reputation: ReputationService,
    private readonly notifications: NotificationDataProviderService,
  ) {}

  public init(): void {
    if (this.started || !isPlatformBrowser(this.platformId)) return;
    this.started = true;
    this.subscriptions = new Subscription();
    this.document.addEventListener('visibilitychange', this.onVisibility);
    this.subscriptions.add(this.tokens.user$.pipe(map(user => user?.id || null), distinctUntilChanged()).subscribe(id => {
      this.userId = id;
      this.lastAttempt = 0;
      this.visit();
    }));
    this.subscriptions.add(this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.visit()));
    if (Capacitor.isNativePlatform()) {
      void App.addListener('appStateChange', state => { if (state.isActive) this.visit(); }).then(handle => {
        if (this.started) this.nativeListener = handle; else void handle.remove();
      });
    }
  }

  private visit(): void {
    if (!this.userId || this.pending || this.document.visibilityState === 'hidden' || Date.now() - this.lastAttempt < 60000) return;
    this.lastAttempt = Date.now();
    this.pending = true;
    const userId = this.userId;
    this.subscriptions.add(this.reputation.visit().pipe(finalize(() => { this.pending = false; })).subscribe({
      next: () => { if (this.userId === userId) this.subscriptions.add(this.notifications.getUnreadCount().subscribe({error: () => {}})); },
      // Retry on the next foreground/navigation event. The server owns the date and deduplication.
      error: () => { this.lastAttempt = 0; },
    }));
  }

  public destroy(): void {
    if (!this.started) return;
    this.started = false;
    this.subscriptions.unsubscribe();
    this.document.removeEventListener('visibilitychange', this.onVisibility);
    void this.nativeListener?.remove();
    this.nativeListener = undefined;
    this.userId = null;
    this.lastAttempt = 0;
    this.pending = false;
  }
}
